import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type CmsPageAppearanceConfigState = {
  formId: string;
  formPrefix?: string;
};

export type CmsPageAppearanceSaveHandlers = {
  submitAll: () => void;
  isSaveDisabled?: boolean;
};

type CmsPageAppearanceContextValue = {
  formId: string | null;
  formPrefix?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  setConfig: (config: CmsPageAppearanceConfigState) => void;
  setFormPrefix: (formPrefix: string | undefined) => void;
  clearConfig: () => void;
  saveHandlers: CmsPageAppearanceSaveHandlers | null;
  registerSaveHandlers: (handlers: CmsPageAppearanceSaveHandlers | null) => void;
};

const CmsPageAppearanceContext =
  createContext<CmsPageAppearanceContextValue | null>(null);

export function CmsPageAppearanceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [formId, setFormId] = useState<string | null>(null);
  const [formPrefix, setFormPrefixState] = useState<string | undefined>(
    undefined,
  );
  const [open, setOpen] = useState(false);
  const [saveHandlers, setSaveHandlers] =
    useState<CmsPageAppearanceSaveHandlers | null>(null);

  const registerSaveHandlers = useCallback(
    (handlers: CmsPageAppearanceSaveHandlers | null) => {
      setSaveHandlers(handlers);
    },
    [],
  );

  const setConfig = useCallback((config: CmsPageAppearanceConfigState) => {
    setFormId(config.formId);
    if (config.formPrefix !== undefined) {
      setFormPrefixState(config.formPrefix);
    }
  }, []);

  const setFormPrefix = useCallback((prefix: string | undefined) => {
    setFormPrefixState(prefix);
  }, []);

  const clearConfig = useCallback(() => {
    setFormId(null);
    setFormPrefixState(undefined);
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      formId,
      formPrefix,
      open,
      setOpen,
      setConfig,
      setFormPrefix,
      clearConfig,
      saveHandlers,
      registerSaveHandlers,
    }),
    [
      formId,
      formPrefix,
      open,
      setConfig,
      setFormPrefix,
      clearConfig,
      saveHandlers,
      registerSaveHandlers,
    ],
  );

  return (
    <CmsPageAppearanceContext.Provider value={value}>
      {children}
    </CmsPageAppearanceContext.Provider>
  );
}

export function useCmsPageAppearance() {
  const ctx = useContext(CmsPageAppearanceContext);
  if (!ctx) {
    throw new Error(
      "useCmsPageAppearance must be used within CmsPageAppearanceProvider",
    );
  }
  return ctx;
}

/** Dispatches a synthetic input event so CMSLayout dirty tracking works for drawer fields. */
export function dispatchCmsFormDirty(formId: string | undefined) {
  if (!formId) return;
  const formEl = document.getElementById(formId);
  if (formEl instanceof HTMLFormElement) {
    formEl.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
